import React, { useState, useEffect } from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";


import HeaderSection from "./components/HeaderSection";
import EmisorSection from "./components/EmisorSection";
import ClienteSection from "./components/ClienteSection";
import DgiSection from "./components/DgiSection";
import ItemsTable from "./components/ItemsTable";
import ItbmsSection from "./components/ItbmsSection";
import TotalsSection from "./components/TotalsSection";
import PaymentSection from "./components/PaymentSection";
import LegalFooter from "./components/LegalFooter";

import GeistRegular from "./fonts/Geist/static/Geist-Regular.ttf";
import GeistMedium from "./fonts/Geist/static/Geist-Medium.ttf";
import GeistBold from "./fonts/Geist/static/Geist-Bold.ttf";
import { RenderIf } from "../../utils/RenderIf";
import QRCode from 'qrcode';


Font.register({
  family: "Geist",
  fonts: [
    { src: GeistRegular, fontWeight: 400 },
    { src: GeistMedium, fontWeight: 500 },
    { src: GeistBold, fontWeight: 700 },
  ],
});

const EMPTY_IMAGE = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+/p9sAAAAASUVORK5CYII=";
const COLORS = {
  softBlue: "#EFF6FF",     // fondo azul pálido
  deepBlue: "#1E3A8A",     // texto azul oscuro
  borderBlue: "#BFDBFE",   // borde suave
};
const S = StyleSheet.create({
  page: {
    fontFamily: "Geist",
    fontSize: 9,
    color: "#111",
    paddingTop: 28,
    paddingRight: 28,
    paddingBottom: 28,
    paddingLeft: 28,
    backgroundColor: "#fff",
  },

  row: { flexDirection: "row" },
  col: { flexGrow: 1, flexBasis: 0 },

  header: {
    position: "relative",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 50,
  },

  brandTitle: { fontSize: 12, fontWeight: 700 },
  label: {
    fontSize: 9,
    color: COLORS.deepBlue,
    textTransform: "uppercase",
    marginBottom: 2,
    fontWeight: 800
  },
  muted: { color: "#6b7280" },
  bold: { fontWeight: 700 },

  qrBox: {
    width: 92,
    height: 92,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderStyle: "dashed",
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  qrText: { fontSize: 7, color: "#64748b", textAlign: "center", paddingLeft: 6, paddingRight: 6 },

  section: { marginTop: 10 },
  box: {
    borderWidth: 1,
    borderColor: COLORS.borderBlue,
    backgroundColor: COLORS.softBlue,
    borderRadius: 8,
    paddingTop: 10,
    paddingRight: 12,
    paddingBottom: 5,
    paddingLeft: 12,
  },

  // Table
  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 6,
    overflow: "hidden",
  },
  thead: {
    flexDirection: "row",
    backgroundColor: "#0f172a",
    paddingTop: 7,
    paddingRight: 8,
    paddingBottom: 7,
    paddingLeft: 8,
  },
  th: { color: "#fff", fontWeight: 700, fontSize: 8 },

  tr: {
    flexDirection: "row",
    paddingTop: 7,
    paddingRight: 8,
    paddingBottom: 7,
    paddingLeft: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
  },
  td: { fontSize: 9 },

  cImg: {
    width: 40, justifyContent: "center",
    alignItems: "center",
  },

  cNo: { width: 22 },
  cDesc: { flex: 1 },
  cQty: {
    width: 46, textAlign: "right", justifyContent: "center",
    alignItems: "center",
  },
  cPU: {
    width: 90,
    textAlign: "right",
    paddingRight: 8, justifyContent: "center",
    alignItems: "center",
  },

  cDU: {
    width: 90,
    textAlign: "right",
    paddingLeft: 8, justifyContent: "center",
    alignItems: "center",
  },

  cItbms: {
    width: 70,
    textAlign: "right", justifyContent: "center",
    alignItems: "center",
  },
  cItem: { width: 70, textAlign: "right", fontWeight: 700 },

  split: {
    marginTop: 10,
    flexDirection: "row",
    gap: 10,
  },
  half: { flexGrow: 1, flexBasis: 0 },

  miniTable: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 6,
    overflow: "hidden",
  },
  miniHead: {
    backgroundColor: "#111827",
    paddingTop: 6,
    paddingRight: 8,
    paddingBottom: 6,
    paddingLeft: 8,
  },
  miniHeadTxt: { color: "#fff", fontWeight: 700, fontSize: 8 },
  miniRow: {
    flexDirection: "row",
    paddingTop: 6,
    paddingRight: 8,
    paddingBottom: 6,
    paddingLeft: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
  },

  totalsBox: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 6,
    overflow: "hidden",
  },
  totRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 7,
    paddingRight: 10,
    paddingBottom: 7,
    paddingLeft: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
  },
  totFinal: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#0f172a",
    color: "#fff",
    paddingTop: 10,
    paddingRight: 10,
    paddingBottom: 10,
    paddingLeft: 10,
    fontSize: 11,
    fontWeight: 700,
  },

  noteLink: {
    marginTop: 8,
    fontSize: 8,
    color: "#334155",
  },

  footer: {
    marginTop: 14,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    fontSize: 8,
    color: "#6b7280",
  },

  pageNumber: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "#6b7280",
  },

  imageLogo: {
    width: 90,
    height: 70,
    marginVertical: 1,
    objectFit: 'cover',
    alignSelf: 'center',
  },


  imageQr: {
    width: 135,
    height: 135,
    marginVertical: 1,
    objectFit: 'cover',
    // position: 'absolute',
    // top: '0',
    // right: '0',
  },

  imageContainer: {
    width: 90,
    height: 70,
    justifyContent: "center",
    alignItems: "center",
  },

  imageFit: {
    objectFit: "contain",
    width: "100%",
    height: "100%",
  },


  imageContainerTable: {
    width: 25,
    height: 25,
    justifyContent: "center",
    alignItems: "center",
  },

  imageFitTable: {
    objectFit: "contain",
    width: "100%",
    height: "100%",
    borderRadius: 10,
  }
});


export default function InvoicePDF({ data }) {
  const items = data.items || [];
  const total = data.total;
  const totalImpuesto = data.tax;
  const totalDescuento = data.discount;
  const totalNeto = data.subtotal; // según tu ejemplo
  const valorTotal = (data.subtotal + data.discount); // según tu ejemplo
  const shippingTotal = data.delivery?.value || 0; // valor del envío, si existe
  const itbmsRows = data.itbmsBreakdown || [
    { montoBase: 0, porcentaje: 0, impuesto: 0, label: "Total" },
  ];

  let doc_type = data.typeDocument;
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  useEffect(() => {
    const qrCodeCanvas = document.createElement('canvas');
    QRCode.toCanvas(qrCodeCanvas, data.consultaClaveUrl, { width: 150, errorCorrectionLevel: 'H' });
    setQrCodeDataUrl(qrCodeCanvas.toDataURL('image/png'));
  }, [data]);

  return (
    <Document>
      <Page size="A4" style={S.page}>

        <HeaderSection S={S} data={data} doc_type={doc_type} />
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 3 }}>
            <EmisorSection S={S} data={data} />
            <ClienteSection S={S} data={data} />
          </View>
          <View style={{ flex: 1 }}>
            {/* QR A LA DERECHA */}
            <View style={{ width: 100 }}>
              <Image
                style={S.imageQr}
                src={qrCodeDataUrl || EMPTY_IMAGE} />
            </View>
          </View>
        </View>

        <RenderIf isTrue={doc_type !== "quote"}>
          <DgiSection S={S} data={data} />
        </RenderIf>

        <ItemsTable S={S} items={items} />
      
        <View style={S.split}>
          <ItbmsSection S={S} itbmsRows={itbmsRows} />
          <TotalsSection
            S={S}
            valorTotal={valorTotal}
            totalNeto={totalNeto}
            totalImpuesto={totalImpuesto}
            total={total}
            totalDescuento={totalDescuento}
            shippingTotal={shippingTotal}
          />
        </View>

  {/* Nota general */}
        {data.note && (
          <View style={{ marginTop: 8, marginBottom: 4, padding: 8, backgroundColor: '#EFF6FF', borderRadius: 6, borderWidth: 1, borderColor: '#BFDBFE' }}>
            <Text style={{ fontSize: 10, color: '#1E3A8A', fontWeight: 'bold', marginBottom: 2 }}>Nota:</Text>
            <Text style={{ fontSize: 9, color: '#334155' }}>{data.note}</Text>
          </View>
        )}


        <RenderIf isTrue={doc_type !== "quote"}><PaymentSection S={S} data={data} /></RenderIf>
        <Text
          style={S.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Página ${pageNumber} de ${totalPages}`
          }
          fixed
        />

        <RenderIf isTrue={doc_type !== "quote"}>
          <LegalFooter S={S} />
        </RenderIf>
      </Page>
    </Document>
  );
}

